import PropTypes from 'prop-types'
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Text,
} from '@chakra-ui/react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useTranslation} from 'react-i18next'
import {SupportAgentIcon} from '../../../icons'

import DropdownSelect from '../../DropdownSelect'
import FileUpload from '../../FileUpload'
import IconTooltip from '../../IconTooltip'
import DirectDebitRequestServiceAgreementModal from '../../ZeptoDirectDebitRequestModals/DirectDebitRequestServiceAgreementModal'
import DirectDebitRequestModal from '../../ZeptoDirectDebitRequestModals/DirectDebitRequestModal'

const ACCOUNT_TYPE_OPTIONS = [
  {label: 'Savings', value: 'savings'},
  {label: 'Transaction', value: 'transaction'},
  {label: 'Business', value: 'business'},
]

const buildValidationSchema = (t) =>
  Yup.object().shape({
    bank: Yup.string().required('Please enter your bank'),
    accountType: Yup.object()
      .nullable()
      .required('Please select your account type'),
    accountName: Yup.string().required('Please enter the account name'),
    bsb: Yup.string().required('Please enter your BSB'),
    accountNumber: Yup.string().required('Please enter your account number'),
    statementFile: Yup.mixed()
      .nullable()
      .required(t('modal.getHelp.uploadRequiredError')),
  })

const invalidHoverStyle = (touched, error) => ({
  borderColor: touched && error ? 'mld.alert.700' : 'mld.primary.200',
})

const GetHelpScreen = ({onSubmitted, onCancel}) => {
  const {t} = useTranslation('connectPayoutAccount')

  const formatBsb = (value) => {
    const digitsOnly = value.replace(/\D/g, '')
    const limited = digitsOnly.slice(0, 6)

    if (limited.length > 3) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`
    }

    return limited
  }

  const formik = useFormik({
    initialValues: {
      bank: '',
      accountType: null,
      accountName: '',
      bsb: '',
      accountNumber: '',
      statementFile: null,
      directDebitAuthorization: false,
    },
    validationSchema: buildValidationSchema(t),
    onSubmit: (values) => onSubmitted(values),
  })

  const handleBsbChange = (e) => {
    const formatted = formatBsb(e.target.value)
    formik.setFieldValue('bsb', formatted)
  }

  const handleAccountNumberChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 9)
    formik.setFieldValue('accountNumber', digitsOnly)
  }

  return (
    <Flex justify="center" maxH="560px" overflowY="auto">
      <Box width="400px">
        <Flex
          as="form"
          flexDirection="column"
          gap={3}
          pt={8}
          pb={8}
          onSubmit={formik.handleSubmit}
        >
          <Text
            color="black"
            fontWeight="regular"
            fontSize="2xl"
            lineHeight="90%"
          >
            {t('modal.getHelp.title')}
          </Text>

          <Text
            color="black"
            fontWeight="medium"
            fontSize="sm"
            lineHeight="125%"
            pb={2}
          >
            {t('modal.getHelp.subtitle')}
          </Text>

          <FormControl
            isInvalid={Boolean(formik.touched.bank && formik.errors.bank)}
          >
            <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
              {t('modal.getHelp.bankLabel')}
            </FormLabel>
            <Input
              name="bank"
              value={formik.values.bank}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              borderRadius="md"
              height="48px"
              _hover={invalidHoverStyle(
                formik.touched.bank,
                formik.errors.bank,
              )}
            />
            <FormErrorMessage>{formik.errors.bank}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={Boolean(
              formik.touched.accountType && formik.errors.accountType,
            )}
          >
            <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
              {t('modal.getHelp.accountTypeLabel')}
            </FormLabel>
            <Box
              sx={
                formik.touched.accountType && formik.errors.accountType
                  ? {
                      '.dd-select-accountType__control': {
                        borderColor: 'var(--chakra-colors-mld-alert-700)',
                      },
                    }
                  : {}
              }
            >
              <DropdownSelect
                name="accountType"
                placeholder={t('modal.getHelp.accountTypePlaceholder')}
                options={ACCOUNT_TYPE_OPTIONS}
                value={formik.values.accountType}
                onChange={(option) => {
                  formik.setFieldValue('accountType', option)
                  formik.setFieldTouched('accountType', true, false)
                }}
                isSearchable
              />
            </Box>
            <FormErrorMessage>{formik.errors.accountType}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={Boolean(
              formik.touched.accountName && formik.errors.accountName,
            )}
          >
            <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
              {t('modal.getHelp.accountNameLabel')}
            </FormLabel>
            <Input
              name="accountName"
              value={formik.values.accountName}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              borderRadius="md"
              height="48px"
              _hover={invalidHoverStyle(
                formik.touched.accountName,
                formik.errors.accountName,
              )}
            />
            <FormErrorMessage>{formik.errors.accountName}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={Boolean(formik.touched.bsb && formik.errors.bsb)}
          >
            <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
              {t('modal.getHelp.bsbLabel')}
            </FormLabel>
            <Input
              name="bsb"
              value={formik.values.bsb}
              onChange={handleBsbChange}
              onBlur={formik.handleBlur}
              borderRadius="md"
              height="48px"
              _hover={invalidHoverStyle(formik.touched.bsb, formik.errors.bsb)}
            />
            <FormErrorMessage>{formik.errors.bsb}</FormErrorMessage>
          </FormControl>

          <FormControl
            isInvalid={Boolean(
              formik.touched.accountNumber && formik.errors.accountNumber,
            )}
          >
            <FormLabel fontSize="sm" fontWeight="medium" mb={1}>
              {t('modal.getHelp.accountNumberLabel')}
            </FormLabel>
            <Input
              name="accountNumber"
              value={formik.values.accountNumber}
              onChange={handleAccountNumberChange}
              onBlur={formik.handleBlur}
              borderRadius="md"
              height="48px"
              _hover={invalidHoverStyle(
                formik.touched.accountNumber,
                formik.errors.accountNumber,
              )}
            />
            <FormErrorMessage>{formik.errors.accountNumber}</FormErrorMessage>
          </FormControl>

          <Box>
            <Text fontSize="sm" fontWeight="medium" mb={1}>
              {t('modal.getHelp.uploadLabel')}
            </Text>
            <Text
              color="black"
              fontWeight="medium"
              fontSize="xs"
              lineHeight="125%"
              mb={2}
            >
              {t('modal.getHelp.uploadHint')}
            </Text>

            <FileUpload
              value={formik.values.statementFile}
              onChange={(file) => formik.setFieldValue('statementFile', file)}
              onRemove={() => formik.setFieldValue('statementFile', null)}
              error={
                formik.touched.statementFile && formik.errors.statementFile
              }
              prompt={t('modal.getHelp.uploadPrompt')}
              subtext={t('modal.getHelp.uploadSubtext')}
              accept=".pdf,application/pdf"
              allowedTypes={['application/pdf']}
              typeErrorMessage="Only PDF files are accepted"
            />
          </Box>

          <Box>
            <Checkbox
              name="directDebitAuthorization"
              isChecked={formik.values.directDebitAuthorization}
              onChange={(e) =>
                formik.setFieldValue(
                  'directDebitAuthorization',
                  e.target.checked,
                )
              }
              variant="mld"
              sx={{
                '.chakra-checkbox__control': {
                  _hover: {
                    borderColor: 'mld.neutral.900',
                  },
                },
              }}
            >
              <Text textStyle="body-small" color="mld.neutral.900">
                {t('modal.getHelp.checkbox.label')}
                <IconTooltip title={t('modal.getHelp.checkbox.tooltip.title')}>
                  <Text textStyle="body-small">
                    {t('modal.getHelp.checkbox.tooltip.body')}
                  </Text>
                </IconTooltip>
              </Text>
            </Checkbox>
            <Flex
              pl={7}
              mt="xs"
              flexDirection="row"
              alignItems="center"
              gap="xxs"
            >
              <Text fontSize="xs">
                <DirectDebitRequestServiceAgreementModal
                  triggerLabel={t('modal.getHelp.directDebitRequest')}
                />
              </Text>
              <Text fontSize="xs">{t('modal.getHelp.and')}</Text>
              <Text fontSize="xs">
                <DirectDebitRequestModal
                  triggerLabel={t(
                    'modal.getHelp.directDebitRequestServiceAgreement',
                  )}
                />
              </Text>
            </Flex>
          </Box>

          <Flex flexDirection="column" backgroundColor="mld.neutral.50" p={4}>
            <Flex flexDirection="column" gap={3} align="flex-start">
              <Flex gap={3} align="center">
                <SupportAgentIcon boxSize="6" flexShrink={0} />
                <Text fontSize="sm" fontWeight="bold">
                  {t('modal.getHelp.weWillBeInTouch')}
                </Text>
              </Flex>
              <Text fontSize="xs" color="mld.neutral.700" lineHeight="125%">
                {t('modal.getHelp.weWillBeInTouchDescription')}
              </Text>
            </Flex>
          </Flex>

          <Flex gap={5} pt={2}>
            <Button
              borderRadius="full"
              variant="primary"
              type="submit"
              isDisabled={!formik.values.directDebitAuthorization}
              _hover={{}}
            >
              {t('modal.buttons.continue')}
            </Button>
            <Button
              borderRadius="full"
              variant="secondary"
              onClick={onCancel}
              _hover={{}}
            >
              {t('modal.buttons.cancel')}
            </Button>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  )
}

GetHelpScreen.propTypes = {
  onSubmitted: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

export default GetHelpScreen
